<script>
import InputChip from "@/components/input-chip.vue";
import Pagination from "@/components/pagination.vue";
import { mapGetters } from "vuex";
import workflowService from "@/api/ativacaoped/workflow.service";
import responsaveisService from "@/api/ativacaoped/responsaveis.service";
import BusinessCard from "./components/business-card.vue";

export default {
  components: { BusinessCard, Pagination, InputChip },
  data() {
    return {
      businessPlans: [],
      filters: {
        revisores: [],
        title: [],
        workflows: []
      },
      paginationMeta: {
        currentPage: 1,
        from: 1,
        lastPage: 1,
        perPage: 15,
        to: 1,
        total: 1
      },
      revisores: [],
      showFilter: false,
      workflows: []
    };
  },
  computed: {
    ...mapGetters(["clientId"])
  },
  async created() {
    if (this.clientId === 0) {
      this.goToHome();
      return;
    }

    const requests = [
      this.initializeLocalWorkflow(),
      this.getReviewers(),
      this.doLoad()
    ];
    
    await Promise.allSettled(requests);
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
      } else {
        this.doLoad();
      }
    }
  },
  methods: {
    async doLoad() {
      try {
        const { revisores, title, workflows } = this.filters;
        const { currentPage, perPage } = this.paginationMeta;
        const params = [`page=${currentPage}`, `per_page=${perPage}`];

        if (Array.isArray(revisores) && revisores.length > 0) {
          params.push(`filters[responsaveis]=${revisores.join(";")}`);
        }

        if (Array.isArray(title) && title.length > 0) {
          params.push(`filters[nome]=${title.join(";")}`);
        }

        if (Array.isArray(workflows) && workflows.length > 0) {
          params.push(`filters[workflow_id]=${workflows.join(";")}`);
        }

        const query = params.join("&");
        const response = await workflowService.getBusinessPlansWorkflow(
          this.clientId,
          query
        );
        this.businessPlans = response?.data.sort(
          (
            { nome: nomeA, sla_label: slaA },
            { nome: nomeB, sla_label: slaB }
          ) => {
            if (slaA === "Atrasado" && slaB !== "Atrasado") {
              return -1;
            }

            if (slaA !== "Atrasado" && slaB === "Atrasado") {
              return 1;
            }

            if (slaA !== "Atrasado" && slaB !== "Atrasado") {
              const [diasA, diasB] = [slaA, slaB].map(sla => {
                try {
                  return parseInt(sla.split(" ")[0]);
                } catch {
                  return 0;
                }
              });

              if (diasA !== diasB) {
                return diasA - diasB;
              }
            }

            return nomeA.localeCompare(nomeB);
          }
        );

        if (response?.meta) {
          const {
            current_page,
            from,
            last_page,
            per_page,
            to,
            total
          } = response.meta;
          this.paginationMeta = {
            currentPage: current_page,
            from,
            lastPage: last_page,
            perPage: per_page,
            to,
            total
          };
        }
      } catch (error) {
        this.notify(error);

        this.businessPlans = [];
      }
    },
    async getReviewers() {
      try {
        const response = await responsaveisService.getSelecaoResponsaveis(
          this.clientId
        );
        this.revisores = response
          .sort(({ nome: a }, { nome: b }) => a.localeCompare(b))
          .map(({ email, id, nome }) => ({ id, label: `${nome} (${email})` }));
      } catch (error) {
        this.revisores = [];
        this.notify(error);
      }
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    },
    /**
     * Recupera a lista de etapas da API e atribui os dados necessários para o
     * filtro de Workflow do acompanhamento.
     */
    async initializeLocalWorkflow() {
      try {
        const etapas = await workflowService.getWorkflows(this.clientId);

        this.workflows = (etapas || []).map(({ id, nome }) => ({ id, nome }));
      } catch {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro ao recuperar a lista de etapas do workflow",
          text: "Por favor, atualize a página."
        });
      }
    }
  }
};
</script>

<template>
  <v-card class="workflow-containers" :color="tableColor" elevation="0">
    <v-card-text class="d-flex">
      <div
        aria-label="refreshButton"
        class="table-v-action-button mr-2"
        @click="doLoad"
      >
        <v-icon>mdi-refresh</v-icon>
        Atualizar
      </div>
      <div class="table-v-action-button mr-2" @click="showFilter = !showFilter">
        <v-icon>mdi-filter-plus-outline</v-icon> Filtros
      </div>
    </v-card-text>
    <transition name="vertical-expand">
      <v-container v-show="showFilter" class="pt-0 pb-3 px-3 w-100" fluid>
        <v-row>
          <v-col class="pt-0" :cols="12" :md="3">
            <v-label>Título do BP</v-label>
            <InputChip
              v-model="filters.title"
              append-icon="mdi-format-text"
              hide-details
              placeholder="Separe termos por ; ou Enter"
            ></InputChip>
          </v-col>

          <v-col class="pt-0" :cols="12" :md="4">
            <v-label>Revisores</v-label>
            <v-autocomplete
              v-model="filters.revisores"
              :append-icon="!revisores.length ? null : '$dropdown'"
              deletable-chips
              dense
              :disabled="!revisores.length"
              hide-details
              :items="revisores"
              item-value="id"
              item-text="label"
              multiple
              outlined
              persistent-placeholder
              :placeholder="
                !revisores.length
                  ? 'Nenhuma opção carregada'
                  : 'Busque por termos'
              "
              :menu-props="{ offset: true, top: true }"
              small-chips
            ></v-autocomplete>
          </v-col>

          <v-col class="pt-0" :cols="12" :md="4">
            <v-label>Workflow</v-label>
            <v-autocomplete
              v-model="filters.workflows"
              :append-icon="!workflows.length ? null : '$dropdown'"
              deletable-chips
              dense
              :disabled="!workflows.length"
              hide-details
              :items="workflows"
              item-value="id"
              item-text="nome"
              multiple
              outlined
              persistent-placeholder
              :placeholder="
                !workflows.length
                  ? 'Nenhuma opção carregada'
                  : 'Busque por termos'
              "
              :menu-props="{ offset: true, top: true }"
              small-chips
            ></v-autocomplete>
          </v-col>

          <v-col align-self="end" class="pt-0 pb-md-5" :cols="12" :md="1">
            <v-btn color="#fff" outlined icon @click="doLoad()">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </transition>

    <div class="cards-container">
      <div class="no-data" v-if="!businessPlans.length">
        Não há business plans preenchidos para exibir.
      </div>

      <BusinessCard
        v-for="plan in businessPlans"
        class="business-card"
        :key="plan.id"
        :value="plan"
      />
    </div>

    <div class="pagination-container">
      <Pagination
        v-model="paginationMeta.currentPage"
        :length="paginationMeta.lastPage"
        @change="doLoad()"
      />
    </div>
  </v-card>
</template>

<style lang="scss" scoped>
.workflow-containers {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 8rem);
}

.cards-container {
  background-color: #fff;
  flex-grow: 1;
  overflow-y: auto;
  padding: 1rem 2.75rem;

  .business-card {
    margin-bottom: 1rem;
  }
}

.pagination-container {
  background-color: #fff;
  border-top: thin solid #00000040;
  display: flex;
  flex-direction: row-reverse;
  padding: 0.5rem 1rem;
}

.no-data {
  width: 100%;
  text-align: center;
}

.table-v-action-button {
  user-select: none;
}

.v-autocomplete::v-deep .v-input__control {
  background-color: #fff;

  .v-select__slot {
    height: 44px;
  }

  .v-input__append-inner.v-input__append-inner {
    margin-top: 10px;
  }
}

.v-label {
  font-size: 0.75rem;
}

.input-chip::v-deep .input-chip__container {
  background-color: #fff;
  border-color: #00000061;
}

/* Animação de expansão/encolhimento vertical com fade */
.vertical-expand-enter-active,
.vertical-expand-leave-active {
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
  overflow: hidden;
  transform-origin: top center;
}

.vertical-expand-enter,
.vertical-expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: scaleY(0);
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.vertical-expand-enter-to,
.vertical-expand-leave {
  max-height: 96px; /* Ajuste conforme necessário */
  opacity: 1;
  transform: scaleY(1);
}

/* Remove padding/margin durante a animação para evitar espaços vazios */
.vertical-expand-enter .v-row,
.vertical-expand-leave-to .v-row {
  opacity: 0;
  transition: opacity 0.2s ease;
}
</style>
